import React, { useState } from 'react';
import { leadsAPI } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const QuickAddLead = ({ isOpen, onClose, onSuccess }) => {
    const { isDark } = useTheme();
    const [formData, setFormData] = useState({
        source_platform: 'zillow',
        lead_name: '',
        lead_email: '',
        lead_phone: '',
        property_address: '',
        lead_message: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        if (!formData.lead_name || !formData.lead_email || !formData.lead_phone || !formData.property_address) {
            setError('Please fill in all required fields');
            return false;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.lead_email)) {
            setError('Please enter a valid email address');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) return;

        try {
            setLoading(true);
            await leadsAPI.createLead(formData);

            // Reset form
            setFormData({
                source_platform: 'zillow',
                lead_name: '',
                lead_email: '',
                lead_phone: '',
                property_address: '',
                lead_message: ''
            });

            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error('Error creating lead:', err);
            setError(err.response?.data?.error || 'Failed to create lead. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div
                className="w-full max-w-2xl bg-mint dark:bg-navy rounded-xl shadow-2xl border border-teal/20 dark:border-light/10 overflow-hidden transform transition-all text-left"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-teal/10 dark:border-light/10 flex items-center justify-between bg-teal/5 dark:bg-light/5">
                    <h2 className="text-xl font-bold text-darkTeal dark:text-light flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary dark:text-cyan">person_add</span>
                        Quick Add Lead
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-darkTeal/50 dark:text-light/50 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto max-h-[80vh]">
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-500 rounded-lg text-sm flex items-center gap-2">
                            <span className="material-symbols-outlined text-lg">error</span>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Source Platform */}
                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-xs font-semibold uppercase text-darkTeal/70 dark:text-light/70 mb-1">Source Platform <span className="text-red-500">*</span></label>
                                <select
                                    name="source_platform"
                                    value={formData.source_platform}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 bg-white dark:bg-navy border border-teal/20 dark:border-light/20 rounded-lg text-darkTeal dark:text-light focus:border-primary dark:focus:border-cyan focus:ring-1 focus:ring-primary dark:focus:ring-cyan outline-none transition-all"
                                >
                                    <option value="zillow">Zillow</option>
                                    <option value="rightmove">Rightmove</option>
                                    <option value="99acres">99acres</option>
                                    <option value="redfin">Redfin</option>
                                    <option value="realtor">Realtor</option>
                                    <option value="facebook">Facebook</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            {/* Lead Name */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-darkTeal/70 dark:text-light/70 mb-1">Lead Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="lead_name"
                                    value={formData.lead_name}
                                    onChange={handleChange}
                                    placeholder="John Doe"
                                    className="w-full px-4 py-2.5 bg-white dark:bg-navy border border-teal/20 dark:border-light/20 rounded-lg text-darkTeal dark:text-light focus:border-primary dark:focus:border-cyan focus:ring-1 focus:ring-primary dark:focus:ring-cyan outline-none transition-all placeholder:text-darkTeal/30 dark:placeholder:text-light/30"
                                />
                            </div>

                            {/* Lead Email */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-darkTeal/70 dark:text-light/70 mb-1">Lead Email <span className="text-red-500">*</span></label>
                                <input
                                    type="email"
                                    name="lead_email"
                                    value={formData.lead_email}
                                    onChange={handleChange}
                                    placeholder="john@example.com"
                                    className="w-full px-4 py-2.5 bg-white dark:bg-navy border border-teal/20 dark:border-light/20 rounded-lg text-darkTeal dark:text-light focus:border-primary dark:focus:border-cyan focus:ring-1 focus:ring-primary dark:focus:ring-cyan outline-none transition-all placeholder:text-darkTeal/30 dark:placeholder:text-light/30"
                                />
                            </div>

                            {/* Lead Phone */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-darkTeal/70 dark:text-light/70 mb-1">Phone Number <span className="text-red-500">*</span></label>
                                <input
                                    type="tel"
                                    name="lead_phone"
                                    value={formData.lead_phone}
                                    onChange={handleChange}
                                    placeholder="+1 (555) 000-0000"
                                    className="w-full px-4 py-2.5 bg-white dark:bg-navy border border-teal/20 dark:border-light/20 rounded-lg text-darkTeal dark:text-light focus:border-primary dark:focus:border-cyan focus:ring-1 focus:ring-primary dark:focus:ring-cyan outline-none transition-all placeholder:text-darkTeal/30 dark:placeholder:text-light/30"
                                />
                            </div>

                            {/* Property Address */}
                            <div>
                                <label className="block text-xs font-semibold uppercase text-darkTeal/70 dark:text-light/70 mb-1">Property Address <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="property_address"
                                    value={formData.property_address}
                                    onChange={handleChange}
                                    placeholder="123 Main St, City, State"
                                    className="w-full px-4 py-2.5 bg-white dark:bg-navy border border-teal/20 dark:border-light/20 rounded-lg text-darkTeal dark:text-light focus:border-primary dark:focus:border-cyan focus:ring-1 focus:ring-primary dark:focus:ring-cyan outline-none transition-all placeholder:text-darkTeal/30 dark:placeholder:text-light/30"
                                />
                            </div>

                            {/* Lead Message */}
                            <div className="col-span-1 md:col-span-2">
                                <label className="block text-xs font-semibold uppercase text-darkTeal/70 dark:text-light/70 mb-1">Message (Optional)</label>
                                <textarea
                                    name="lead_message"
                                    value={formData.lead_message}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Any additional details..."
                                    className="w-full px-4 py-2.5 bg-white dark:bg-navy border border-teal/20 dark:border-light/20 rounded-lg text-darkTeal dark:text-light focus:border-primary dark:focus:border-cyan focus:ring-1 focus:ring-primary dark:focus:ring-cyan outline-none transition-all placeholder:text-darkTeal/30 dark:placeholder:text-light/30 resize-none"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-start gap-3 mt-6 pt-4 border-t border-teal/10 dark:border-light/10">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 rounded-lg text-darkTeal/70 dark:text-light/70 hover:bg-teal/5 dark:hover:bg-light/5 font-semibold transition-colors text-sm"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2.5 bg-primary dark:bg-cyan text-white dark:text-navy font-bold rounded-lg shadow-lg hover:shadow-xl hover:bg-primary/90 dark:hover:bg-cyan/90 transition-all transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                            >
                                {loading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined text-lg">check</span>
                                        Add Lead
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default QuickAddLead;
